/**
 * vr_data — the replicated rows the VR panel reads, reduced to plain values (XR P2 "VR UI / UX", 2026-09-28).
 *
 * Every read is MY rows through the owner / identity index (lib/fast_find answers those as Map lookups — never a walk
 * of a shard-wide table per frame, the FIND-IS-A-SCAN law), with a guarded walk as the fallback for a table the
 * bindings do not index. Nothing here writes; the only write the panel ever makes is `swapWeapon` (Inventory → Equip),
 * the same one reducer call the desktop weapon ring makes.
 */

import { atlas } from '../../state.js';
import { WEAPONS, FISTS, type WeaponSpec } from '../../lib/weapon_catalog.js';
import type { FeedRow, InvItemIn, InvStackIn } from '../../lib/xr_ui_data.js';

type Owned = { owner: { isEqual(o: unknown): boolean } };
type Db = Record<string, Record<string, unknown> & { iter?: () => Iterable<unknown> }>;

function db(): Db | null { return (atlas.conn?.db as unknown as Db) ?? null; }

function walk<T>(table: string): Iterable<T> {
  const t = db()?.[table] as { iter?: () => Iterable<T> } | undefined;
  try { return t?.iter ? t.iter() : []; } catch { return []; }
}

/** MY rows of a table through its owner btree; the filtered walk when the index is missing. */
export function mine<T extends Owned>(table: string, index: string): T[] {
  const conn = atlas.conn;
  if (!conn?.identity) return [];
  const t = db()?.[table] as Record<string, { filter?(v: unknown): Iterable<T> }> | undefined;
  try {
    const idx = t?.[index];
    if (idx && typeof idx.filter === 'function') return [...idx.filter(conn.identity)];
  } catch { /* fall back to the walk */ }
  const me = conn.identity;
  return [...walk<T>(table)].filter((r) => r.owner.isEqual(me));
}

/** MY row of an identity-keyed table (player_vitals, weapon_state, wallet, pulse). */
export function myRow<T extends { identity: { isEqual(o: unknown): boolean } }>(table: string): T | null {
  const conn = atlas.conn;
  if (!conn?.identity) return null;
  const t = db()?.[table] as { identity?: { find?(v: unknown): T | null | undefined } } | undefined;
  try {
    const r = t?.identity?.find?.(conn.identity);
    if (r !== undefined) return r ?? null;
  } catch { /* fall back */ }
  for (const r of walk<T>(table)) if (r.identity.isEqual(conn.identity)) return r;
  return null;
}

export function vitals(): { hp: number; maxHp: number } {
  const v = myRow<{ identity: { isEqual(o: unknown): boolean }; hp: number; maxHp: number }>('playerVitals');
  return { hp: v?.hp ?? 100, maxHp: v?.maxHp ?? 100 };
}

/** The weapon in hand (the desktop combat HUD's own rule: the `weapon` slot of player_equipment, else bare hands). */
export function equipped(): { spec: WeaponSpec; quality: number } {
  for (const eq of mine<Owned & { slot: string; itemKey: string; quality: number }>('playerEquipment', 'playerEquipmentByOwner')) {
    if (eq.slot === 'weapon' && WEAPONS[eq.itemKey]) return { spec: WEAPONS[eq.itemKey], quality: eq.quality };
  }
  return { spec: FISTS, quality: 0 };
}

export function feed(): FeedRow | null {
  const r = myRow<{ identity: { isEqual(o: unknown): boolean }; itemKey: string; rounds: number; charge: number; chargeAt: { microsSinceUnixEpoch: bigint }; reloadUntil: { microsSinceUnixEpoch: bigint } }>('weaponState');
  if (!r) return null;
  return {
    itemKey: r.itemKey, rounds: r.rounds, charge: r.charge,
    chargeAtMs: Number(r.chargeAt.microsSinceUnixEpoch / 1000n),
    reloadUntilMs: Number(r.reloadUntil.microsSinceUnixEpoch / 1000n),
  };
}

export function inventory(): { items: InvItemIn[]; stacks: InvStackIn[] } {
  const items = mine<Owned & { itemId: bigint; itemKey: string; name: string; quality: number }>('playerItem', 'playerItemByOwner')
    .map((it) => ({ itemId: it.itemId, itemKey: it.itemKey, name: it.name, quality: it.quality, isWeapon: !!WEAPONS[it.itemKey] }));
  const stacks = mine<Owned & { stackId: bigint; name: string; familyKey: string; qty: number }>('playerResource', 'playerResourceByOwner')
    .filter((s) => s.qty > 0)
    .map((s) => ({ stackId: s.stackId, name: s.name, familyKey: s.familyKey, qty: s.qty }));
  return { items, stacks };
}

export function wallet(): { sov: string; inf: string } {
  const w = myRow<{ identity: { isEqual(o: unknown): boolean }; sov: bigint; inf: bigint }>('wallet');
  return { sov: w ? w.sov.toString() : '—', inf: w ? w.inf.toString() : '—' };
}

export function pulse(): { social: number; fun: number; creativity: number; rest: number } | null {
  return myRow<{ identity: { isEqual(o: unknown): boolean }; social: number; fun: number; creativity: number; rest: number }>('pulse');
}

/** Hours:minutes of the world's sky (0.5 = noon when no row is replicated). */
export function timeOfDay(): string {
  let tod = 0.5;
  const conn = atlas.conn;
  try { if (conn && atlas.currentWorldId !== null) tod = conn.db.skyState.worldId.find(atlas.currentWorldId)?.timeOfDay ?? 0.5; } catch { /* pre-publish */ }
  const h = Math.floor(tod * 24), m = Math.floor((tod * 24 - h) * 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function worldName(): string {
  const conn = atlas.conn;
  try { return (conn && atlas.currentWorldId !== null ? conn.db.world.worldId.find(atlas.currentWorldId)?.name : '') ?? ''; } catch { return ''; }
}

/** My owned worlds (the old panel's "My Worlds"), the Commons excluded. */
export function myWorlds(): Array<{ worldId: bigint; name: string; here: boolean }> {
  const conn = atlas.conn;
  const me = atlas.identityHex;
  const out: Array<{ worldId: bigint; name: string; here: boolean }> = [];
  if (!conn || !me) return out;
  for (const w of walk<{ worldId: bigint; owner: { toHexString(): string }; name: string; kind: string }>('world')) {
    if (w.kind === 'commons' || w.owner.toHexString() !== me) continue;
    out.push({ worldId: w.worldId, name: w.name, here: w.worldId === atlas.currentWorldId });
  }
  return out.sort((a, b) => (a.worldId < b.worldId ? -1 : 1));
}
